import { CustomerReportsPanel } from './CustomerReportsPanel';
import { CustomerRemindersPanel } from './CustomerRemindersPanel';
import { CustomerPortalLinkPanel } from './CustomerPortalLinkPanel';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { useToast } from '../hooks/use-toast';
import { Users, Plus, Edit2, Trash2, Loader2, Mail, Phone, MapPin, Search, Building2, Archive, RotateCcw, RefreshCw, CheckCircle2, BarChart3, FileText } from 'lucide-react';
import CustomerSyncDialog from './CustomerSyncDialog';
import CustomerProjectsInline from './CustomerProjectsInline';
import CustomerScorecard from './CustomerScorecard';
import { ColumnLayoutEditor } from './ColumnLayoutEditor';
import { LabelsEditor, DocumentAuditList } from './DocumentLayoutBits';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const emptyForm = { name: '', email: '', phone: '', address: '', billing_address: '', qb_customer_id: '', account_manager_id: '', material_markup_pct: '', billing_currency: '', payment_method: 'invoice', rate_card: [], invoice_terms_days: 30, po_number: '', site_name: '', column_layouts: {} };

export default function CustomersManager({ token, isAdmin }) {
  const { toast } = useToast();
  const [customers, setCustomers] = useState([]);
  const [archived, setArchived] = useState([]);
  const [showArchive, setShowArchive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingArchive, setLoadingArchive] = useState(false);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [colSchema, setColSchema] = useState(null);
  const [presets, setPresets] = useState([]);
  const [custAudit, setCustAudit] = useState([]);
  const [presetToApply, setPresetToApply] = useState('');
  const [presetNote, setPresetNote] = useState('');
  const loadCustomerDocs = (customerId) => {
    const h = { headers: { Authorization: `Bearer ${token}` } };
    axios.get(`${API}/company/document-presets`, h).then((r) => setPresets(r.data.presets || [])).catch(() => {});
    axios.get(`${API}/customers/${customerId}/document-audit?limit=10`, h).then((r) => setCustAudit(r.data.items || [])).catch(() => setCustAudit([]));
  };
  const applyPreset = async () => {
    if (!presetToApply || !editing) return;
    try {
      const res = await axios.post(`${API}/customers/${editing.id}/apply-document-preset`, { preset_id: presetToApply, note: presetNote }, { headers: { Authorization: `Bearer ${token}` } });
      setForm((f) => ({ ...f, column_layouts: res.data.column_layouts || {}, document_labels: res.data.document_labels || {} }));
      setPresetToApply(''); setPresetNote('');
      toast({ title: `Preset “${res.data.applied}” applied`, description: 'Copied into this customer. Edits to the preset later will not change this customer.' });
      loadCustomerDocs(editing.id);
      fetchCustomers();
    } catch (e) {
      toast({ title: 'Could not apply preset', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };
  const [colDoc, setColDoc] = useState('daily');
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/document-columns/schema`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setColSchema(r.data)).catch(() => {});
  }, [token]);
  const [saving, setSaving] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(null);
  const [archiveReason, setArchiveReason] = useState('');
  const [confirmHardDelete, setConfirmHardDelete] = useState(null);
  // Account managers — populated once for the dropdown in the Edit dialog.
  // Only active admins/supervisors qualify.
  const [accountManagerOptions, setAccountManagerOptions] = useState([]);
  // QBO connection status + sync dialog state (Feb 2026, Phase D)
  const [qbConnected, setQbConnected] = useState(false);
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  // "Also create in QBO" checkbox for the customer create form
  const [alsoPushCustomerToQbo, setAlsoPushCustomerToQbo] = useState(false);
  const [pushingCustomerToQbo, setPushingCustomerToQbo] = useState(false);
  // Customer profitability scorecard (Jun 2026)
  const [scorecardFor, setScorecardFor] = useState(null);

  // Load the candidate account managers once on mount
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : (data?.users || []);
        setAccountManagerOptions(
          list.filter((u) => ['admin', 'supervisor'].includes(u.role) && u.active !== false),
        );
      })
      .catch(() => setAccountManagerOptions([]));
  }, [token]);

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/customers`, { headers: { Authorization: `Bearer ${token}` } });
      setCustomers(res.data || []);
    } catch (e) {
      toast({ title: 'Failed to load customers', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [token, toast]);

  const fetchArchived = useCallback(async () => {
    try {
      setLoadingArchive(true);
      const res = await axios.get(`${API}/customers/archived`, { headers: { Authorization: `Bearer ${token}` } });
      setArchived(res.data || []);
    } catch (e) {
      toast({ title: 'Failed to load archive', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setLoadingArchive(false);
    }
  }, [token, toast]);

  useEffect(() => { if (token) fetchCustomers(); }, [token, fetchCustomers]);

  // QBO connection status — drives the visibility of the Sync / Push buttons.
  // We hit /qb/status (returns {connected:bool}) and silently fall back to
  // "not connected" on any error.
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/qb/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        const connected = !!(data?.connected ?? data?.qb_connected);
        setQbConnected(connected);
        setAlsoPushCustomerToQbo(connected);
      })
      .catch(() => setQbConnected(false));
  }, [token]);

  const handleArchive = async () => {
    if (!confirmArchive) return;
    try {
      await axios.delete(`${API}/customers/${confirmArchive.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: archiveReason.trim() ? { archive_reason: archiveReason.trim() } : undefined,
      });
      toast({ title: 'Customer archived', description: 'Moved to the customer archive (kept for 7 years).' });
      setConfirmArchive(null);
      setArchiveReason('');
      fetchCustomers();
      if (showArchive) fetchArchived();
    } catch (e) {
      toast({ title: 'Failed to archive', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const handleHardDelete = async () => {
    if (!confirmHardDelete) return;
    try {
      await axios.delete(`${API}/customers/${confirmHardDelete.id}?hard=true`, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Customer permanently deleted' });
      setConfirmHardDelete(null);
      fetchArchived();
    } catch (e) {
      toast({ title: 'Failed to delete', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const handleRestore = async (c) => {
    if (!window.confirm(`Restore ${c.name} to active customers?`)) return;
    try {
      await axios.post(`${API}/customers/${c.id}/restore`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Customer restored', description: `${c.name} is back in your active list.` });
      fetchArchived();
      fetchCustomers();
    } catch (e) {
      toast({ title: 'Failed to restore', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const openCreate = () => { setEditing(null); setForm(emptyForm); setShowDialog(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name || '', email: c.email || '', phone: c.phone || '', address: c.address || '', billing_address: c.billing_address || '', qb_customer_id: c.qb_customer_id || '', account_manager_id: c.account_manager_id || '', material_markup_pct: c.material_markup_pct != null ? String(c.material_markup_pct) : '', billing_currency: c.billing_currency || '', payment_method: c.payment_method || 'invoice', rate_card: c.rate_card || [], invoice_terms_days: c.invoice_terms_days ?? 30, po_number: c.po_number || '', site_name: c.site_name || '', column_layouts: c.column_layouts || {}, document_labels: c.document_labels || {}, change_note: '' }); setShowDialog(true); loadCustomerDocs(c.id); };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Name is required', variant: 'destructive' });
      return;
    }
    try {
      setSaving(true);
      const payload = {
        name: form.name.trim(),
        email: form.email || undefined,
        phone: form.phone || undefined,
        address: form.address || undefined,
        billing_address: form.billing_address || undefined,
        qb_customer_id: form.qb_customer_id || undefined,
        // Empty string clears the field server-side (handled in CustomerUpdate)
        account_manager_id: form.account_manager_id || '',
        // Phase 2 (Feb 2026) — per-customer markup override (null = inherit company default)
        material_markup_pct: form.material_markup_pct === '' ? null : parseFloat(form.material_markup_pct),
        billing_currency: form.billing_currency || 'auto',
        payment_method: form.payment_method || 'invoice',
      };
      const rateCardBody = { items: (form.rate_card || []).filter((r) => r.code && r.name).map((r) => ({ code: r.code.trim(), name: r.name.trim(), rate: Number(r.rate) || 0, badge_code: r.badge_code || null })),
        terms_days: Number(form.invoice_terms_days) || 0, po_number: form.po_number || '', site_name: form.site_name || '',
        column_layouts: Object.fromEntries(['daily', 'invoice', 'estimate'].map((d) => [d, (form.column_layouts || {})[d] || null])),
        document_labels: Object.fromEntries(['daily', 'invoice', 'estimate'].map((d) => [d, (form.document_labels || {})[d] || null])),
        change_note: form.change_note || '' };
      if (editing) {
        const { data: updated } = await axios.put(`${API}/customers/${editing.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        await axios.put(`${API}/customers/${editing.id}/rate-card`, rateCardBody, { headers: { Authorization: `Bearer ${token}` } });
        if (updated?._qb_warning) {
          toast({ title: 'Saved locally — QBO sync warning', description: updated._qb_warning, variant: 'default' });
        } else {
          toast({ title: 'Customer updated' });
        }
      } else {
        // Feb 2026 — pass ?push_to_qbo=true when the admin ticked the box.
        const push = alsoPushCustomerToQbo && qbConnected ? '?push_to_qbo=true' : '';
        const { data: created } = await axios.post(`${API}/customers${push}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        if (created?._qb_warning) {
          toast({ title: 'Created locally — QBO sync warning', description: created._qb_warning, variant: 'default' });
        } else if (created?.qb_customer_id && alsoPushCustomerToQbo) {
          toast({ title: 'Customer created · pushed to QBO', description: `QBO Customer #${created.qb_customer_id}` });
        } else {
          toast({ title: 'Customer created' });
        }
      }
      setShowDialog(false);
      fetchCustomers();
    } catch (e) {
      toast({ title: 'Failed to save', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handlePushCustomerToQbo = async () => {
    if (!editing?.id) return;
    setPushingCustomerToQbo(true);
    try {
      const { data } = await axios.post(
        `${API}/customers/${editing.id}/push-to-quickbooks`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({ title: 'Pushed to QBO', description: `QBO Customer #${data.qb_customer_id}` });
      // Reflect the new qb_customer_id in the open form so the badge updates.
      setForm((f) => ({ ...f, qb_customer_id: data.qb_customer_id }));
      setEditing((c) => (c ? { ...c, qb_customer_id: data.qb_customer_id } : c));
      fetchCustomers();
    } catch (e) {
      toast({ title: 'Push to QBO failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setPushingCustomerToQbo(false);
    }
  };

  // Customer-level "Send portal links via SMS" — finds all of this
  // customer's active jobs, auto-generates portal tokens for any that
  // don't have one yet, and texts ONE consolidated SMS with every link.
  // The phone is taken from the current form field (so unsaved edits are
  // honored) and falls back to the persisted customer.phone server-side.
  const [sendingPortalSms, setSendingPortalSms] = useState(false);
  const handleSendCustomerPortalSms = async () => {
    if (!editing?.id) return;
    const phone = (form.phone || '').trim();
    if (!phone) {
      toast({
        title: 'Phone required',
        description: 'Add a phone number for this customer before sending a portal link.',
        variant: 'destructive',
      });
      return;
    }
    setSendingPortalSms(true);
    try {
      const { data } = await axios.post(
        `${API}/customers/${editing.id}/portal/send-sms`,
        { phone },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({
        title: `Portal links texted to ${data.sent_to}`,
        description: `${data.job_count} active job${data.job_count === 1 ? '' : 's'} included.`,
      });
    } catch (e) {
      toast({
        title: 'Could not send portal links',
        description: e.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setSendingPortalSms(false);
    }
  };

  const handleDelete = async () => {
    // Deprecated: use handleArchive (soft) or handleHardDelete (admin override)
  };

  // suppress unused-var warning for legacy stub
  void handleDelete;

  const filtered = (showArchive ? archived : customers).filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.name || '').toLowerCase().includes(q)
      || (c.email || '').toLowerCase().includes(q)
      || (c.phone || '').toLowerCase().includes(q);
  });

  return (
    <Card className="shadow-lg" data-testid="customers-manager">
      <CardHeader className="bg-[#1A2433] border-b flex flex-row items-center justify-between gap-3 flex-wrap">
        <CardTitle className="text-lg font-bold text-white flex items-center gap-2">
          <Users className="w-5 h-5" style={{ color: '#1E88E5' }} />
          {showArchive ? `Archived Customers (${archived.length})` : `Customers (${customers.length})`}
        </CardTitle>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button
              size="sm"
              variant={showArchive ? 'default' : 'outline'}
              onClick={() => {
                const next = !showArchive;
                setShowArchive(next);
                if (next) fetchArchived();
              }}
              className={showArchive ? 'bg-[#1E88E5] hover:bg-blue-700' : ''}
              data-testid="customers-archive-toggle"
            >
              <Archive className="w-4 h-4 mr-1.5" />
              {showArchive ? 'View Active' : 'View Archive'}
            </Button>
          )}
          {!showArchive && qbConnected && isAdmin && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowSyncDialog(true)}
              className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
              data-testid="customers-sync-qbo-btn"
              title="Pull customers from QuickBooks"
            >
              <RefreshCw className="w-4 h-4 mr-1.5" />
              Sync from QBO
            </Button>
          )}
          {!showArchive && (
            <Button onClick={openCreate} data-testid="customer-add-btn" style={{ background: '#1E88E5' }}>
              <Plus className="w-4 h-4 mr-1" /> Add Customer
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4 space-y-4">
        <div className="relative max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder={showArchive ? 'Search archive...' : 'Search customers...'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="customer-search"
          />
        </div>

        {(showArchive ? loadingArchive : loading) ? (
          <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10 text-slate-400" data-testid="customers-empty">
            <Building2 className="w-10 h-10 mx-auto mb-2 opacity-40" />
            {showArchive ? (archived.length === 0 ? 'No archived customers.' : 'No archived customers match your search.')
              : (customers.length === 0 ? 'No customers yet. Add your first customer.' : 'No customers match your search.')}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="customers-list">
            {filtered.map((c) => (
              <div key={c.id} className="rounded-xl p-4 border" style={{ background: '#0F1923', borderColor: '#2D3748' }} data-testid={`customer-card-${c.id}`}>
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white font-semibold truncate">{c.name}</p>
                      {showArchive && <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">Archived</span>}
                    </div>
                    <div className="mt-1 space-y-1 text-sm" style={{ color: '#A0AEC0' }}>
                      {c.email && <div className="flex items-center gap-2 truncate"><Mail className="w-3.5 h-3.5 flex-shrink-0" />{c.email}</div>}
                      {c.phone && <div className="flex items-center gap-2 truncate"><Phone className="w-3.5 h-3.5 flex-shrink-0" />{c.phone}</div>}
                      {c.address && <div className="flex items-center gap-2 truncate"><MapPin className="w-3.5 h-3.5 flex-shrink-0" /><span className="truncate"><span className="text-[10px] uppercase tracking-wider mr-1.5 opacity-60">Service</span>{c.address}</span></div>}
                      {c.billing_address && <div className="flex items-center gap-2 truncate"><MapPin className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#FFA000' }} /><span className="truncate"><span className="text-[10px] uppercase tracking-wider mr-1.5 opacity-60" style={{ color: '#FFA000' }}>Billing</span>{c.billing_address}</span></div>}
                      {c.qb_customer_id && <div className="text-xs flex items-center gap-1.5"><span className="px-1.5 py-0.5 rounded font-mono" style={{ background: '#1A2433', color: '#1E88E5' }}>QB</span>{c.qb_customer_id}</div>}
                      {showArchive && c.deleted_at && (
                        <div className="text-xs">Archived {new Date(c.deleted_at).toLocaleDateString()} by {c.deleted_by_name || 'admin'}</div>
                      )}
                      {showArchive && c.archive_reason && (
                        <div className="text-xs italic" style={{ color: '#A78BFA' }}>Reason: {c.archive_reason}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    {showArchive ? (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => handleRestore(c)} data-testid={`customer-restore-${c.id}`} className="text-[#00C896] hover:text-green-400" title="Restore to active">
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                        {isAdmin && (
                          <Button size="sm" variant="ghost" onClick={() => setConfirmHardDelete(c)} data-testid={`customer-hard-delete-${c.id}`} className="text-red-400 hover:text-red-300" title="Permanently delete">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </>
                    ) : (
                      <>
                        {isAdmin && (
                          <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`customer-statement-${c.id}`} className="text-[#90CAF9] hover:text-white" title="Statement & reports to customer">
                            <FileText className="w-4 h-4" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => setScorecardFor(c)} data-testid={`customer-scorecard-${c.id}`} className="text-[#00C896] hover:text-green-400" title="Profitability scorecard">
                          <BarChart3 className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`customer-edit-${c.id}`} className="text-slate-300" title="Edit customer">
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        {isAdmin && (
                          <Button size="sm" variant="ghost" onClick={() => { setConfirmArchive(c); setArchiveReason(''); }} data-testid={`customer-archive-${c.id}`} className="text-amber-400 hover:text-amber-300" title="Archive customer (7-year retention)">
                            <Archive className="w-4 h-4" />
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Create/Edit dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] flex flex-col" data-testid="customer-dialog">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>{editing ? 'Edit Customer' : 'Add Customer'}</DialogTitle>
            <DialogDescription>Customers can be assigned to jobs and appear in revenue reports.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 flex-1 overflow-y-auto pr-1">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="customer-name-input" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="customer-email-input" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="customer-phone-input" />
            </div>
            <div>
              <Label>Service Address</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Where work is performed" data-testid="customer-address-input" />
            </div>
            <div>
              <Label>Billing Address</Label>
              <Input value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} placeholder="Where invoices are sent (if different)" data-testid="customer-billing-address-input" />
            </div>
            <div>
              <Label>Billing currency</Label>
              <select
                value={form.billing_currency}
                onChange={(e) => setForm({ ...form, billing_currency: e.target.value })}
                className="w-full h-10 rounded-md border px-3 text-sm"
                style={{ background: '#1A2433', color: '#fff', borderColor: '#2D3748' }}
                data-testid="customer-billing-currency-select"
              >
                <option value="">Auto — detect from billing address country</option>
                {['CAD', 'USD', 'GBP', 'EUR', 'AUD', 'NZD', 'MXN', 'DKK', 'SEK', 'NOK', 'CHF'].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <p className="text-xs text-gray-500 mt-1">Every job and invoice for this customer is billed in this currency unless overridden on the invoice.</p>
            </div>
            {editing && (
              <div className="border rounded-lg p-3 space-y-2" style={{ borderColor: '#2D3748' }} data-testid="customer-rate-card">
                <div className="flex items-center justify-between">
                  <Label>Daily-invoice rate card (bill code → name, rate, customer badge / vendor #)</Label>
                  <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, rate_card: [...(form.rate_card || []), { code: '', name: '', rate: '', badge_code: '' }] })} data-testid="rate-card-add">+ Code</Button>
                </div>
                {(form.rate_card || []).map((r, i) => (
                  <div key={i} className="grid grid-cols-[80px_1fr_90px_1fr_32px] gap-1.5 items-center" data-testid={`rate-card-row-${i}`}>
                    {[['code', 'SUPV'], ['name', 'Standard Pay SUPV'], ['rate', '45.17'], ['badge_code', 'FH_12SUPV_NS']].map(([k, ph]) => (
                      <Input key={k} value={r[k] ?? ''} placeholder={ph} type={k === 'rate' ? 'number' : 'text'} step="0.01"
                        onChange={(e) => setForm({ ...form, rate_card: form.rate_card.map((x, idx) => (idx === i ? { ...x, [k]: e.target.value } : x)) })}
                        data-testid={`rate-card-${k}-${i}`} />
                    ))}
                    <button type="button" onClick={() => setForm({ ...form, rate_card: form.rate_card.filter((_, idx) => idx !== i) })} className="text-red-400 text-sm" data-testid={`rate-card-remove-${i}`}>✕</button>
                  </div>
                ))}
                <div className="grid grid-cols-3 gap-2 pt-1">
                  <div><Label className="text-xs">Terms (days)</Label><Input type="number" value={form.invoice_terms_days} onChange={(e) => setForm({ ...form, invoice_terms_days: e.target.value })} data-testid="customer-terms-days" /></div>
                  <div><Label className="text-xs">P.O. number</Label><Input value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} data-testid="customer-po-number" /></div>
                  <div><Label className="text-xs">Site name</Label><Input value={form.site_name} onChange={(e) => setForm({ ...form, site_name: e.target.value })} data-testid="customer-site-name" /></div>
                </div>
                {colSchema && (
                  <div className="pt-3 mt-2 border-t space-y-2" style={{ borderColor: '#2D3748' }} data-testid="customer-columns-section">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div>
                        <Label className="text-xs">Document columns for this customer</Label>
                        <p className="text-[11px] text-slate-500">Leave on company default, or set the headers/columns this customer's upload platform expects (e.g. NAME → BILL CODE).</p>
                      </div>
                      <div className="flex gap-1">
                        {colSchema.doc_types.map((d) => (
                          <button key={d.key} type="button" onClick={() => setColDoc(d.key)}
                            className={`px-2 py-1 rounded text-[11px] border ${colDoc === d.key ? 'border-blue-500 bg-blue-500/10 text-blue-200' : 'border-slate-700 text-slate-300'}`}
                            data-testid={`customer-columns-tab-${d.key}`}>
                            {d.label}{(form.column_layouts || {})[d.key] ? ' •' : ''}
                          </button>
                        ))}
                      </div>
                    </div>
                    {presets.length > 0 && (
                      <div className="flex flex-wrap items-end gap-2 rounded-md border border-slate-700 px-3 py-2" data-testid="customer-preset-apply-block">
                        <div className="flex-1 min-w-[160px]">
                          <Label className="text-[10px] uppercase tracking-wide text-slate-500">Apply a saved preset (all document types)</Label>
                          <select value={presetToApply} onChange={(e) => setPresetToApply(e.target.value)} className="w-full h-8 rounded-md px-2 text-xs" style={{ background: '#1A2433', color: 'white', border: '1px solid #2D3748' }} data-testid="customer-preset-select">
                            <option value="">Choose preset…</option>
                            {presets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                        </div>
                        <div className="flex-1 min-w-[160px]">
                          <Label className="text-[10px] uppercase tracking-wide text-slate-500">Why (optional)</Label>
                          <Input value={presetNote} maxLength={500} onChange={(e) => setPresetNote(e.target.value)} className="h-8 text-xs" placeholder="e.g. Customer switched upload platform" data-testid="customer-preset-note" />
                        </div>
                        <Button type="button" size="sm" variant="outline" onClick={applyPreset} disabled={!presetToApply} data-testid="customer-preset-apply">Apply preset</Button>
                      </div>
                    )}
                    {!(form.column_layouts || {})[colDoc] ? (
                      <div className="flex items-center justify-between rounded-md border border-slate-700 px-3 py-2">
                        <span className="text-xs text-slate-300">Using company default columns</span>
                        <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, column_layouts: { ...(form.column_layouts || {}), [colDoc]: (colSchema.company?.[colDoc] || colSchema.defaults[colDoc]).map((c) => ({ ...c })) } })} data-testid={`customer-columns-customize-${colDoc}`}>
                          Customize for this customer
                        </Button>
                      </div>
                    ) : (
                      <ColumnLayoutEditor
                        docType={colDoc}
                        value={form.column_layouts[colDoc]}
                        defaults={colSchema.defaults[colDoc]}
                        sources={colSchema.sources[colDoc]}
                        onChange={(next) => setForm({ ...form, column_layouts: { ...(form.column_layouts || {}), [colDoc]: next } })}
                        testIdPrefix="customer-cols"
                        compact
                      />
                    )}
                    <div className="pt-2">
                      <Label className="text-xs">Header &amp; totals labels for this customer</Label>
                      <LabelsEditor docType={colDoc} fields={colSchema.label_fields[colDoc]} defaults={{ ...colSchema.label_defaults[colDoc], ...(((colSchema.company_labels || {})[colDoc]) || {}) }}
                        value={(form.document_labels || {})[colDoc]} onChange={(next) => setForm({ ...form, document_labels: { ...(form.document_labels || {}), [colDoc]: next } })} testIdPrefix="customer-labels" />
                    </div>
                    <div className="pt-2">
                      <Label className="text-xs">Change note (optional — why this change)</Label>
                      <Input value={form.change_note || ''} maxLength={500} onChange={(e) => setForm({ ...form, change_note: e.target.value })} className="h-8 text-xs" placeholder="Saved with the audit entry" data-testid="customer-change-note" />
                    </div>
                    <div className="pt-2" data-testid="customer-audit">
                      <Label className="text-xs">Recent changes (who · what · when · why)</Label>
                      <DocumentAuditList items={custAudit} testIdPrefix="customer-audit" />
                    </div>
                  </div>
                )}
              </div>
            )}
            <div>
              <Label>How this customer pays</Label>
              <select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                className="w-full h-10 rounded-md border px-3 text-sm" style={{ background: '#1A2433', color: '#fff', borderColor: '#2D3748' }}
                data-testid="customer-payment-method-select">
                <option value="invoice">Pay by invoice / bank transfer (remit-to shown in portal)</option>
                <option value="stripe">Online with Stripe (Pay now button in portal)</option>
                <option value="both">Both — Pay now plus remit-to instructions</option>
              </select>
            </div>
            <div>
              <Label>QuickBooks Customer ID</Label>
              <div className="flex gap-2">
                <Input
                  value={form.qb_customer_id}
                  onChange={(e) => setForm({ ...form, qb_customer_id: e.target.value })}
                  placeholder={qbConnected ? 'Auto-filled when pushed to QBO' : 'Optional — for QBO sync'}
                  data-testid="customer-qb-id-input"
                />
                {editing && qbConnected && !form.qb_customer_id && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handlePushCustomerToQbo}
                    disabled={pushingCustomerToQbo}
                    className="flex-shrink-0 border-blue-500/40 text-blue-300 hover:bg-blue-500/10"
                    data-testid="customer-push-to-qbo-btn"
                  >
                    {pushingCustomerToQbo
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <><RefreshCw className="w-3.5 h-3.5 mr-1" /> Push to QBO</>}
                  </Button>
                )}
                {editing && form.qb_customer_id && (
                  <span
                    className="flex-shrink-0 inline-flex items-center gap-1 px-2 rounded text-xs bg-emerald-500/15 text-emerald-300"
                    data-testid="customer-linked-badge"
                  >
                    <CheckCircle2 className="w-3 h-3" /> Linked
                  </span>
                )}
              </div>
            </div>
            {!editing && qbConnected && (
              <label className="flex items-center gap-2 text-xs text-slate-300 -mt-1" data-testid="customer-also-push-row">
                <input
                  type="checkbox"
                  checked={alsoPushCustomerToQbo}
                  onChange={(e) => setAlsoPushCustomerToQbo(e.target.checked)}
                  className="accent-blue-500"
                  data-testid="customer-also-push-checkbox"
                />
                Also create this customer in QuickBooks
              </label>
            )}
            <div>
              <Label>Account Manager</Label>
              <select
                value={form.account_manager_id}
                onChange={(e) => setForm({ ...form, account_manager_id: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-md text-sm"
                style={{ background: '#0F1923', border: '1px solid #2D3748', color: '#E2E8F0' }}
                data-testid="customer-account-manager-select"
              >
                <option value="">— No account manager —</option>
                {accountManagerOptions.map((u) => (
                  <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                ))}
              </select>
              <p className="text-[10px] mt-1" style={{ color: '#64748B' }}>
                Auto-watches every new job for this customer so they get notifications without flooding every admin.
              </p>
            </div>
            <div>
              <Label>Material markup % override</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                max="500"
                value={form.material_markup_pct}
                onChange={(e) => setForm({ ...form, material_markup_pct: e.target.value })}
                placeholder="Blank = use company default"
                data-testid="customer-material-markup-input"
              />
              <p className="text-[10px] mt-1" style={{ color: '#64748B' }}>
                Discount customers can be billed at a lower markup; premium customers at higher. Leave blank to inherit the company default. Per-job + per-line overrides still win.
              </p>
            </div>

            {/* Customer Portal — admin one-click flow. Texts the customer
                a secure link covering all their active jobs in a single
                SMS. Auto-generates portal tokens server-side where
                missing. Only shown when editing (need a saved customer to
                attach to). */}
            {editing && (
              <div
                className="pt-3 border-t border-[#2D3748]"
                data-testid="customer-portal-section"
              >
                <div className="flex items-start gap-3 rounded-lg p-3"
                  style={{ background: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.18)' }}
                >
                  <Phone className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#38BDF8' }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>
                      Customer Portal
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: '#94A3B8' }}>
                      Text this customer secure links so they can monitor every active job in one message.
                    </p>
                    {!form.phone && (
                      <p className="text-[11px] mt-1 italic" style={{ color: '#F59E0B' }}>
                        Add a phone number above to enable.
                      </p>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleSendCustomerPortalSms}
                      disabled={sendingPortalSms || !form.phone}
                      className="mt-2"
                      style={{ background: '#0EA5E9' }}
                      data-testid="customer-portal-send-sms-btn"
                    >
                      {sendingPortalSms ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Phone className="w-3.5 h-3.5 mr-1.5" />}
                      {sendingPortalSms ? 'Sending…' : 'Send portal links via SMS'}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Reports to customer — Sep 2026 (statements, summaries, privacy level, schedule). */}
            {isAdmin && (
              <div className="pt-3 border-t border-[#2D3748]">
                <CustomerReportsPanel customer={editing} token={token} />
              </div>
            )}
            {isAdmin && editing?.id && (
              <div className="pt-3 border-t border-[#2D3748]">
                <CustomerRemindersPanel customer={editing} token={token} />
              </div>
            )}
            {isAdmin && editing?.id && (
              <div className="pt-3 border-t border-[#2D3748]">
                <CustomerPortalLinkPanel customer={editing} token={token} />
              </div>
            )}

            {/* Inline Projects — Feb 2026, Phase D. */}
            <div className="pt-3 border-t border-[#2D3748]">
              <CustomerProjectsInline
                customerId={editing?.id || null}
                token={token}
                qbConnected={qbConnected}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} style={{ background: '#1E88E5' }} data-testid="customer-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editing ? 'Save Changes' : 'Create Customer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive confirm */}
      <Dialog open={!!confirmArchive} onOpenChange={(o) => !o && setConfirmArchive(null)}>
        <DialogContent data-testid="customer-archive-dialog">
          <DialogHeader>
            <DialogTitle>Archive customer?</DialogTitle>
            <DialogDescription>
              <strong>{confirmArchive?.name}</strong> will be moved to the archive (kept for 7 years) and can be restored at any time. Existing jobs and revenue reports keep their historical link.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="archive-reason-input">Archive reason (optional)</Label>
            <Input
              id="archive-reason-input"
              placeholder='e.g. "No longer a client", "Acquired by another company"'
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
              data-testid="customer-archive-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmArchive(null)}>Cancel</Button>
            <Button onClick={handleArchive} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="customer-archive-confirm-btn">
              <Archive className="w-4 h-4 mr-1.5" /> Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hard-delete confirm (only available from Archive view) */}
      <Dialog open={!!confirmHardDelete} onOpenChange={(o) => !o && setConfirmHardDelete(null)}>
        <DialogContent data-testid="customer-hard-delete-dialog">
          <DialogHeader>
            <DialogTitle>Permanently delete customer?</DialogTitle>
            <DialogDescription>
              <strong>{confirmHardDelete?.name}</strong> will be permanently removed from the database. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmHardDelete(null)}>Cancel</Button>
            <Button onClick={handleHardDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="customer-hard-delete-confirm-btn">
              <Trash2 className="w-4 h-4 mr-1.5" /> Permanently Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Sync from QBO dialog (Feb 2026, Phase D) */}
      <CustomerSyncDialog
        open={showSyncDialog}
        onClose={() => setShowSyncDialog(false)}
        token={token}
        onApplied={() => { fetchCustomers(); }}
      />
      <CustomerScorecard customer={scorecardFor} token={token} onClose={() => setScorecardFor(null)} />
    </Card>
  );
}
